/**
 * Choosing a model, and connecting the key that runs it.
 *
 * ONE sheet in two modes, and — the part that matters — ONE key-writing
 * component beneath both of them, `ProviderKeyCard`, whose provider is a PROP
 * and is never inferred.
 *
 * That is a structural claim rather than a stylistic one. Two surfaces that can
 * set a key are two surfaces that can disagree about which provider it belongs
 * to, and a key in the wrong slot has already left the page by the time anything
 * rejects it. The old shape guarded that: the draft lived here, its owner was
 * read off whichever radio was checked, and a line in the radio's `onChange`
 * cleared it when the provider changed. It worked, and it was load-bearing —
 * delete that one line and an Anthropic secret goes to `api.openai.com` on the
 * next Save. Now the draft lives inside the card, beside the prop that names
 * where it goes, and the question the guard answered cannot be asked. A guard can
 * be dropped in a refactor by somebody who does not know what it is for; a
 * parameter cannot be, because nothing compiles without it.
 *
 * THE FIRST RUN IS ONE SHORT SCREEN. A usability study put six newcomers in front
 * of the old splash and all six stumbled: it opened on "Choose a model to start"
 * over nine rows of builder copy, and on a phone the key field sat about four
 * screens down, under some 840 words. So the splash is now the Settings →
 * Providers layout cut to one provider: one sentence of pitch, the model that will
 * run (the registry's default, marked recommended) with its search statement and
 * its one caveat, the key field straight under it, Save and start, and the worked
 * example for anybody without a key. Everything it used to say is still one tap
 * away and none of it was deleted: every other model — each with its own search
 * statement, which is the one thing on the old list a reader could not afford to
 * lose — sits behind "Other models (N)"; routing lives in Settings → Routing, which
 * is where a newcomer who has not run anything yet has no reason to be; and the
 * reasons behind the rows moved to `docs/guide.md` ("Starting").
 *
 * Save on the first run lands on the question box, not on the worked example. The
 * example unfurls behind this sheet, and dismissing the sheet replays it (see
 * `setSetup`), which is right for somebody who came to look and wrong for somebody
 * who just paid for a key: most of the study's personas saved and found
 * themselves on the demo's seed alone, at the start of its replay, rather than on
 * an empty canvas asking what they wanted to figure out. Settings mode keeps its
 * ordinary Close / Save and never moves the canvas.
 */

import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { activeEntry, entryFor, useStore } from '../core/store'
import { DEMO_SESSION_ID } from '../core/persist'
import { loadDemo } from '../engine/demo'
import {
  SELECTABLE_MODELS,
  DEFAULT_MODEL_ID,
  EFFORT_LABEL,
  EFFORT_WORK,
  EFFORT_WORK_LABEL,
  PROVIDERS,
  ROLES,
  ROLE_LABEL,
  findModel,
  houseModelId,
  levelFor,
  searchNote,
  type ModelEntry,
  type ProviderId,
} from '../core/models'
import { Icon } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'
import { focusNodeList } from './cardFocus'
import { ProviderKeyCard, type KeyCardResult } from './ProviderKeyCard'
import { SettingsPanel } from './SettingsPanel'
import { keySavedSentence, type KeyCheck } from '../engine/keyCheck'

export function SetupSheet() {
  const mode = useStore((s) => s.setupOpen)
  const setSetup = useStore((s) => s.setSetup)
  const modelId = useStore((s) => s.modelId)
  const setModel = useStore((s) => s.setModel)
  const roleModels = useStore((s) => s.roleModels)
  const setRoleModel = useStore((s) => s.setRoleModel)
  const effortLevels = useStore((s) => s.effortLevels)
  const setEffortLevel = useStore((s) => s.setEffortLevel)
  const keys = useStore((s) => s.keys)
  const storedBase = useStore((s) => s.customBase)
  const toast = useStore((s) => s.toast)

  // Local until Save. Nothing here commits on a keystroke: choosing a model is a
  // decision about what the next pass costs, and it should take a button.
  const [pick, setPick] = useState(modelId)
  // Routing is a draft too, for the reason above: it decides what the next pass
  // costs and which house bills it, and a select that rewrote that on the way
  // past would be the one control here that commits on a keystroke.
  const [draftRoles, setDraftRoles] = useState(roleModels)
  // How hard each kind of work thinks sits beside its route and is a draft for
  // the same reason (D313): it decides what the next pass draws.
  const [draftEffort, setDraftEffort] = useState(effortLevels)
  // Between Save and the provider's answer on a key just written. Save stands
  // down meanwhile and says so; the key itself is already stored.
  const [checking, setChecking] = useState(false)

  /**
   * The mounted key cards, by provider.
   *
   * The sheet's Save calls these; it never sees a key. That is the whole reason
   * for the indirection — Save has to write everything at once (the key, the
   * endpoint, the selection, the routing) and this is how it does that without
   * ever holding the secret it is committing. What comes back is what happened,
   * which is all the toast needs.
   */
  const commits = useRef(new Map<ProviderId, () => KeyCardResult>())
  const registerKeyCard = useCallback((p: ProviderId, commit: (() => KeyCardResult) | null) => {
    if (commit) commits.current.set(p, commit)
    else commits.current.delete(p)
  }, [])
  // Whether each mounted card could run a pass after Save — a typed or stored
  // key, or an endpoint address that parses. Reported by the card rather than
  // recomputed here, because only the card knows what is in its own field.
  const [ready, setReady] = useState<Partial<Record<ProviderId, boolean>>>({})
  const onKeyCardReady = useCallback(
    (p: ProviderId, r: boolean | null) =>
      setReady((m) => {
        // A retraction DELETES the entry rather than storing `false`, so that
        // `willHaveKey`'s `??` can reach the stored-key fallback below. Written as
        // `false` it read as "reported, and there is no key", which is a different
        // and wrong sentence about a workspace that has one.
        if (r === null) {
          if (!(p in m)) return m
          const next = { ...m }
          delete next[p]
          return next
        }
        return m[p] === r ? m : { ...m, [p]: r }
      }),
    [],
  )

  const dialogRef = useRef<HTMLDivElement | null>(null)
  // Trap focus in the sheet and make the canvas behind it inert. The gate keying
  // on `mode` and not on mount is deliberate — the sheet is mounted for the life
  // of the app and only renders null while closed.
  useModalFocus(dialogRef, mode !== null)

  /**
   * Reset on every OPEN, and this is a correctness fix rather than tidiness.
   *
   * This component is mounted for the life of the app and only returns null while
   * closed, so `useState(modelId)` runs once — at first mount. Without this `pick`
   * would drift from `modelId` the moment the model changed anywhere else, so the
   * sheet would offer a key field for a provider the user was not looking at — the
   * one failure this whole area is built against. The key draft resets under the
   * same rule, one level down, in `ProviderKeyCard`.
   */
  useEffect(() => {
    if (!mode) return
    const st = useStore.getState()
    setPick(st.modelId)
    setDraftRoles(st.roleModels)
    setDraftEffort(st.effortLevels)
  }, [mode])

  if (!mode) return null

  const entry: ModelEntry = findModel(pick) ?? activeEntry({ modelId })
  const provider = PROVIDERS[entry.provider]
  const splash = mode === 'splash'
  const isCustom = provider.custom === true
  // Before the card's first effect has reported, fall back to what is stored —
  // otherwise Save would flicker disabled for one frame on a workspace that
  // already has a key.
  const willHaveKey = ready[entry.provider] ?? (isCustom ? !!storedBase : !!keys[entry.provider])
  // Whether routing alone is reason enough to enable Save. It is: routing is
  // independent of keys, and a user who has re-pointed a role and cannot commit
  // it is looking at a control that took their decision and dropped it. The same
  // holds for a level set beside a route (D313). Settings only now — the first
  // run carries no routing control, so nothing there moves.
  const routingMoved =
    ROLES.some((r) => (draftRoles[r] ?? null) !== (roleModels[r] ?? null)) ||
    EFFORT_WORK.some((w) => (draftEffort[w] ?? null) !== (effortLevels[w] ?? null))
  // Why Save is off, where the pointer already is. A disabled button with no
  // stated reason is a dead end.
  const saveBlocked = isCustom
    ? 'Set the endpoint address to run this'
    : `Paste your ${provider.name} key to run ${entry.name}`

  const close = () => setSetup(null)

  const save = async () => {
    if (checking) return
    const changedModel = pick !== modelId
    // Every mounted card writes its own field. The results say what happened and
    // never what was typed.
    const results = [...commits.current.values()].map((fn) => fn())
    const wrote =
      results.find((r) => r.provider === entry.provider && r.key !== 'none') ??
      results.find((r) => r.key !== 'none')
    const endpoint = results.find((r) => r.endpoint)?.endpoint ?? null
    if (changedModel) setModel(pick)
    // Only the roles that moved. `setRoleModel` writes to localStorage on every
    // call, so replaying all five would mean five writes to say nothing.
    const movedRoles = ROLES.filter(
      (role) => (draftRoles[role] ?? null) !== (roleModels[role] ?? null),
    )
    for (const role of movedRoles) setRoleModel(role, draftRoles[role] ?? null)
    const movedEffort = EFFORT_WORK.filter(
      (work) => (draftEffort[work] ?? null) !== (effortLevels[work] ?? null),
    )
    for (const work of movedEffort) setEffortLevel(work, draftEffort[work] ?? null)

    // The provider's word on the key just written, when its house has a free call
    // to ask (`engine/keyCheck.ts`). Awaited so the sentence below says what
    // happened — "Key works", or "Key saved · checked on your first pass" — rather
    // than "Connected" for any string. A key it turns away keeps the sheet open on
    // the card, which prints the provider's own sentence and takes the caret.
    let verdict: KeyCheck | null = null
    if (wrote?.check) {
      setChecking(true)
      verdict = await wrote.check
      setChecking(false)
    }
    const stillOpen = useStore.getState().setupOpen === mode
    if (verdict?.kind === 'rejected') {
      // Closed while the check was out (Escape, the backdrop): the card that
      // would have said this is gone, so a toast says it, with the way back.
      if (!stillOpen) {
        const provider = wrote!.provider
        toast(`${PROVIDERS[provider].name} rejected that key: ${verdict.message}`, 'warn', {
          label: 'Paste another',
          run: () => useStore.getState().askForKey(provider),
        })
      }
      return
    }

    // Every one of these is 'info'. Connecting a key is a step, not an
    // achievement, and a green toast for it would be the app congratulating
    // itself for being paid.
    if (endpoint) {
      toast(
        `Pointed at ${endpoint}. Every pass goes there from now on — the source check is off, because this app cannot know whether it can search.`,
        'info',
      )
    } else if (wrote) {
      // The model is named only when the key that landed belongs to the house the
      // selection runs on. In settings a second provider's card can be the one
      // that wrote, and a sentence about the next pass would then be a claim
      // about a model this key cannot call.
      toast(
        keySavedSentence({
          house: PROVIDERS[wrote.provider].name,
          replaced: wrote.key === 'replaced',
          verdict,
          ...(wrote.provider === entry.provider ? { runs: entry.name } : {}),
        }),
        'info',
      )
    } else if (changedModel) {
      toast(`The next pass runs on ${entry.name}.`, 'info')
    } else if (movedRoles.length || movedEffort.length) {
      // Otherwise Save is silent for a routing-only change, and a control that
      // took a button and then said nothing reads as one that did not work.
      const named = [
        ...movedRoles.map(
          (r) =>
            `${ROLE_LABEL[r].toLowerCase()} on ${entryFor({ modelId: pick, roleModels: draftRoles }, r).name}`,
        ),
        ...movedEffort.map(
          (w) =>
            `${EFFORT_WORK_LABEL[w].toLowerCase()} at ${EFFORT_LABEL[levelFor(draftEffort, w)]} thinking`,
        ),
      ].join(', ')
      toast(`Routing saved — ${named}.`, 'info')
    }

    // "Start" means the question box. Only over the worked example: that is what
    // a first run has on the canvas, and replacing it costs nothing — it is built
    // under its own id and comes back whole (D122). An exploration of the user's
    // own is left exactly where it is, because the palette can reopen this sheet
    // over one, and saving a key is no reason to take somebody's graph off screen.
    // BEFORE `close()`, so the session is no longer the demo when `setSetup` asks
    // whether to replay it. A refusal (a live run) toasts its own sentence and
    // leaves the canvas alone, which is the right outcome there too. Not when the
    // sheet was closed while the key was being checked: the reader has moved on.
    if (!stillOpen) return
    if (splash && useStore.getState().session.id === DEMO_SESSION_ID) {
      useStore.getState().newExploration()
    }
    close()
    // A key just saved means the next thing this person does is type a question,
    // so the caret goes to the box rather than back to the page body, where the
    // question's first letter would reach the graph's verbs. See `sigCaret`.
    if (wrote) useStore.getState().pokeCaret()
  }

  // The keyless door. Closing the sheet replays the worked example when it is
  // the one on the canvas (see `setSetup`); reopened over something else — the
  // palette can do that — the example has to be loaded first, or the button would
  // say one thing and do nothing.
  const watch = () => {
    const onDemo = useStore.getState().session.id === DEMO_SESSION_ID
    close()
    if (!onDemo) void loadDemo()
    // The caret to the Ask box, as a saved key's is (`sigCaret`). The sheet opens
    // over nothing, so its own restore left focus on the page body: a screen
    // reader was told nothing about where it had landed, and a letter typed there
    // reached the graph's verbs.
    useStore.getState().pokeCaret()
    // A phone takes no caret (it would raise the keyboard over the example), so
    // focus goes to the graph's node list instead — see `focusNodeList`.
    if (window.matchMedia('(max-width: 767px)').matches) focusNodeList()
  }

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="setupTitle"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon
            name={splash ? 'pathways' : 'key'}
            size={18}
            strokeWidth={splash ? 1.5 : undefined}
            style={{ color: 'var(--accent)' }}
          />
          <h2 id="setupTitle">{splash ? 'Bring your toughest question.' : 'Settings'}</h2>
        </div>

        {splash ? (
          <FirstRun
            entry={entry}
            pick={pick}
            setPick={setPick}
            routed={ROLES.filter((r) => roleModels[r]).length}
            canSave={willHaveKey && !checking}
            checking={checking}
            saveBlocked={saveBlocked}
            registerKeyCard={registerKeyCard}
            onKeyCardReady={onKeyCardReady}
            onSave={() => void save()}
            onWatch={watch}
            onRouting={() => setSetup('settings', 'routing')}
          />
        ) : (
          <SettingsPanel
            pick={pick}
            setPick={setPick}
            draftRoles={draftRoles}
            setDraftRoles={setDraftRoles}
            draftEffort={draftEffort}
            setDraftEffort={setDraftEffort}
            registerKeyCard={registerKeyCard}
            onKeyCardReady={onKeyCardReady}
            onSubmit={() => void save()}
          />
        )}

        {/* Settings keeps its footer. The first run has none: its two actions sit
            directly under the key field, so on a phone the whole path — pitch,
            model, key, Save — is on the first screen, and nothing below the fold
            is needed to start. */}
        {!splash && (
          <div className="sheet-actions">
            <button className="btn" onClick={close}>
              Close
            </button>
            <button
              className="btn btn--primary"
              disabled={checking || (!willHaveKey && !routingMoved && pick === modelId)}
              title={willHaveKey || routingMoved || pick !== modelId ? undefined : saveBlocked}
              onClick={() => void save()}
            >
              {checking ? CHECKING : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The first run's body: the model that will run, the key that runs it, and the
 * two ways out — in that order, because that order is the whole journey.
 *
 * The block at the top is the PICK, not the default. It starts on the registry's
 * default because that is what `pick` starts on, and it follows whatever is
 * chosen under "Other models" — the key card below it is keyed on the pick's
 * provider, so choosing a model from another house replaces the card rather than
 * handing it a draft typed for somebody else (see the file header).
 *
 * Everything it acts on arrives as a prop, so a test can render it without a
 * store open on the splash — static markup reads a store's initial state.
 */
/** Save's own label while the provider is asked about the key it just wrote. */
const CHECKING = 'Checking the key…'

export function FirstRun({
  entry,
  pick,
  setPick,
  routed,
  canSave,
  checking = false,
  saveBlocked,
  registerKeyCard,
  onKeyCardReady,
  onSave,
  onWatch,
  onRouting,
}: {
  entry: ModelEntry
  pick: string
  setPick: Dispatch<SetStateAction<string>>
  /** Roles already sent somewhere other than the selection, as STORED. */
  routed: number
  canSave: boolean
  /** Between Save and the provider's answer about the key. */
  checking?: boolean
  saveBlocked: string
  registerKeyCard: (p: ProviderId, commit: (() => KeyCardResult) | null) => void
  onKeyCardReady: (p: ProviderId, ready: boolean | null) => void
  onSave: () => void
  onWatch: () => void
  onRouting: () => void
}) {
  const recommended = findModel(DEFAULT_MODEL_ID)
  // Everything the picker offers except the default, which has the top block to
  // itself. The count is read off the registry, so a model added or hidden (the
  // deployed build hides the endpoint row) changes the number without an edit.
  const others = SELECTABLE_MODELS.filter((m) => m.id !== DEFAULT_MODEL_ID)
  // Open on arrival only when the saved selection is one of these, so the row
  // the top block describes is visible in the list it came from. Read from the
  // store rather than `pick`, which is still last open's value on this first
  // render and is reset by the sheet's effect a frame later. Never bound after
  // that: a disclosure that snapped shut under a click is worse than one left open.
  const [openOthers] = useState(() => useStore.getState().modelId !== DEFAULT_MODEL_ID)

  return (
    <div className="sheet-body scroll">
      <p>
        Give Pathways an idea, a question or a claim, and a model maps the other explanations,
        contradictions and dead ends around it.
      </p>

      <div className="field" style={{ gap: 6 }}>
        <span className="eyebrow">{PROVIDERS[entry.provider].name.toUpperCase()}</span>
        <span className="mrow-head">
          {entry.name}
          {entry.id === DEFAULT_MODEL_ID && (
            <span style={{ fontWeight: 'var(--fw-reg)', color: 'var(--text-3)' }}>
              · recommended
            </span>
          )}
        </span>
        <span className="mrow-note">{searchNote(entry)}</span>
        <span className="mrow-note">{entry.note}</span>
        {entry.id !== DEFAULT_MODEL_ID && recommended && (
          <button
            type="button"
            className="linkish"
            style={{ alignSelf: 'start', fontSize: 'var(--fs-micro)' }}
            onClick={() => setPick(DEFAULT_MODEL_ID)}
          >
            Use {recommended.name}, the recommended model
          </button>
        )}
      </div>

      <ProviderKeyCard
        key={entry.provider}
        provider={entry.provider}
        activeModelName={entry.name}
        register={registerKeyCard}
        onReady={onKeyCardReady}
        onSubmit={onSave}
        // Another house's key pasted here moves the pick to that house, and the
        // card above remounts for it, empty (D335). The reader pastes it again.
        onSwitchHouse={(p) => {
          const id = houseModelId(p)
          if (id) setPick(id)
        }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          className="btn btn--primary btn--tall"
          disabled={!canSave}
          title={canSave || checking ? undefined : saveBlocked}
          onClick={onSave}
        >
          {checking ? CHECKING : 'Save and start'}
        </button>
        <button className="btn btn--outline btn--tall" onClick={onWatch}>
          Watch a worked example (no key)
        </button>
      </div>

      {/* Routing is not on this sheet, but a route already in effect must never
          look untouched (D22) — so if any exist, the sheet says so and names the
          door. Only reachable when someone routed first and reopened this. */}
      {routed > 0 && (
        <p className="gnote">
          {routed === 1 ? 'One kind of work is' : `${routed} kinds of work are`} sent to another
          model.{' '}
          <button type="button" className="linkish" onClick={onRouting}>
            See Routing
          </button>
        </p>
      )}

      <details className="field" open={openOthers || undefined}>
        <summary className="eyebrow" style={{ cursor: 'pointer' }}>
          Other models ({others.length})
        </summary>
        <ul className="mlist" role="radiogroup" aria-label="Other models" style={{ marginTop: 8 }}>
          {others.map((m, i) => {
            const prev = others[i - 1]
            const head = !prev || prev.provider !== m.provider
            return (
              <li key={m.id}>
                {head && (
                  <span className="eyebrow" style={{ display: 'block', margin: '8px 0 6px' }}>
                    {PROVIDERS[m.provider].name}
                  </span>
                )}
                <label className="mrow" data-on={m.id === pick || undefined}>
                  <input
                    className="sr-only"
                    type="radio"
                    name="nv-model"
                    value={m.id}
                    checked={m.id === pick}
                    onChange={() => setPick(m.id)}
                  />
                  <span className="mrow-head">{m.name}</span>
                  <span className="mrow-note">{m.note}</span>
                  {/* Kept on every row. What a model's search returns decides what
                      a source check on it can establish, and it is the one line a
                      chooser cannot learn anywhere else before paying for a pass. */}
                  <span className="mrow-note">{searchNote(m)}</span>
                </label>
              </li>
            )
          })}
        </ul>
      </details>
    </div>
  )
}
